/** @format */
import { Injectable } from '@angular/core';
import { EntityState, EntityStore, QueryEntity, StoreConfig } from '@datorama/akita';
import { NgEntityServiceConfig } from '@datorama/akita-ng-entity-service';
import { ApplicationType, OtmId, StudentApplication } from 'common-typescript/types';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { EntityService } from './entity.service';

// The inherited EntityService.getById() resolves through `GET /ori/api/student-applications?id=…`,
// but ORI's collection endpoint requires `studentId` and returns 400 without it. The detail endpoint
// `GET /ori/api/student-applications/{id}` is what we actually need; findById() targets it directly.

const CONFIG = {
    ENDPOINTS: {
        backend: '/ori/api',
        getApplicationsForStudentByTypes(studentId: OtmId) {
            return `${this.backend}/student-applications/${studentId}/by-types`;
        },
    },
};

@Injectable({
    providedIn: 'root',
})
@NgEntityServiceConfig({
    baseUrl: CONFIG.ENDPOINTS.backend,
    resourceName: 'student-applications',
})
export class StudentApplicationEntityService extends EntityService<StudentApplicationState> {
    constructor() {
        super(StudentApplicationStore, StudentApplicationQuery);
    }

    getApplicationsForStudentByTypes(studentId: OtmId, applicationTypes: ApplicationType[]): Observable<StudentApplication[]> {
        if (!studentId || !applicationTypes?.length) {
            return of([]);
        }

        return this.getHttp()
            .get<StudentApplication[]>(CONFIG.ENDPOINTS.getApplicationsForStudentByTypes(studentId), {
                params: {
                    types: applicationTypes,
                },
            })
            .pipe(
                tap(applications => this.store.upsertMany(applications)),
                switchMap(applications => this.query.selectMany(applications.map(application => application.id))),
            );
    }

    cancel(application: StudentApplication): Observable<StudentApplication> {
        return this.getHttp()
            .post<StudentApplication>(`${this.api}/${application.id}/cancel`, null)
            .pipe(tap(cancelled => this.store.upsert(cancelled.id, cancelled)));
    }

    findById(applicationId: OtmId, bypassStore: boolean = false): Observable<StudentApplication> {
        if (!bypassStore && this.query.hasEntity(applicationId)) {
            return this.query.selectEntity(applicationId);
        }
        return this.getHttp()
            .get<StudentApplication>(`${this.api}/${applicationId}`)
            .pipe(tap(application => this.store.upsert(application.id, application)));
    }
}

type StudentApplicationState = EntityState<StudentApplication, OtmId>;

@StoreConfig({ name: 'student-application' })
class StudentApplicationStore extends EntityStore<StudentApplicationState> {}

class StudentApplicationQuery extends QueryEntity<StudentApplicationState> {
    constructor(protected store: StudentApplicationStore) {
        super(store);
    }
}
